import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormGroup,
  InputLabel,
  TextInput,
  ErrorText,
  ModalCancelButton,
  ModalSubmitButton,
} from './changePasswordModal.styles';
import { verifyEmail, resendEmailVerificationCode } from '../../../services/auth';
import { useAuth } from '../../../hooks/useAuth';

const VerificationEmailModal = ({ isOpen, onClose, email }) => {
  const [code, setCode] = useState('');
  const [errors, setErrors] = useState({});
  const { updateUserData } = useAuth();

  const verifyMutation = useMutation({
    mutationFn: verifyEmail,
    onSuccess: () => {
      // Same email, just confirming ownership — no need to log the user
      // out. Patch the already-held userData so isVerified flips to true
      // and this modal stops being auto-opened by RootLayout.
      updateUserData({ emailVerified: true });
      alert('Email verified successfully.');
      handleClose();
    },
    onError: (err) => {
      setErrors({
        form: err?.detail || err?.message || 'Verification failed. Please check your code.',
      });
    },
  });

  const resendMutation = useMutation({
    mutationFn: resendEmailVerificationCode,
    onSuccess: () => {
      alert('Verification code sent! Please check your email.');
    },
    onError: (err) => {
      setErrors({ form: err?.message || 'Failed to resend code. Please try again.' });
    },
  });

  const handleClose = () => {
    setCode('');
    setErrors({});
    verifyMutation.reset();
    resendMutation.reset();
    onClose();
  };

  const handleVerifySubmit = (e) => {
    e.preventDefault();

    if (!code.trim()) {
      setErrors({ code: 'Verification code is required' });
      return;
    }

    setErrors({});
    verifyMutation.mutate({ email, code: code.trim() });
  };

  const handleResend = () => {
    setErrors({});
    resendMutation.mutate();
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle id="modal-title">Verify email</ModalTitle>
          <ModalCloseButton onClick={handleClose} type="button" aria-label="Close">
            <X size={16} />
          </ModalCloseButton>
        </ModalHeader>

        <form onSubmit={handleVerifySubmit}>
          <ModalBody>
            <FormGroup>
              <InputLabel htmlFor="verify-email-address">Email</InputLabel>
              <TextInput id="verify-email-address" type="email" value={email} readOnly disabled />
            </FormGroup>

            <FormGroup>
              <InputLabel htmlFor="verify-email-code">Verification code</InputLabel>
              <TextInput
                id="verify-email-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Enter the code from your email"
                $hasError={!!errors.code}
                aria-describedby={errors.code ? 'verify-email-code-error' : undefined}
              />
              {errors.code && (
                <ErrorText id="verify-email-code-error" role="alert">
                  {errors.code}
                </ErrorText>
              )}
            </FormGroup>

            <div style={{ fontSize: '0.8125rem' }}>
              Didn&apos;t get a code?{' '}
              <span
                role="button"
                tabIndex={0}
                onClick={!resendMutation.isPending ? handleResend : undefined}
                onKeyDown={(e) =>
                  !resendMutation.isPending &&
                  (e.key === 'Enter' || e.key === ' ') &&
                  handleResend()
                }
                style={{
                  cursor: resendMutation.isPending ? 'default' : 'pointer',
                  opacity: resendMutation.isPending ? 0.6 : 1,
                  textDecoration: 'underline',
                }}
              >
                {resendMutation.isPending ? 'Sending…' : 'Resend code'}
              </span>
            </div>

            {errors.form && <ErrorText role="alert">{errors.form}</ErrorText>}
          </ModalBody>

          <ModalFooter>
            <ModalCancelButton type="button" onClick={handleClose}>
              Cancel
            </ModalCancelButton>
            <ModalSubmitButton type="submit" disabled={verifyMutation.isPending}>
              {verifyMutation.isPending ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : null}
              {verifyMutation.isPending ? 'Verifying…' : 'Verify email'}
            </ModalSubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};

export default VerificationEmailModal;
